import React, { useState } from "react";
import { useParams } from "react-router";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";

import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import { CUSTOM_FIELD_TEMPLATE_CREATE } from "../../constants/graphql/mutations";
import { CUSTOM_FIELD_TEMPLATES_QUERY, FETCH_PROJECT_QUERY } from "../../constants/graphql/queries";
import AssignExistingConfigurableFieldDialog from "./AssignExistingConfigurableFieldDialog";
import ConfigurableFieldFormStep from "./ConfigurableFieldFormStep";

const TEMPLATE_SORT_VARS = { sortBy: "default_label", sortDirection: "asc" };

const customFieldTemplateQueryVariables = projectId => {
  const variableSets = [{}, TEMPLATE_SORT_VARS];
  if (projectId) {
    variableSets.push({ ...TEMPLATE_SORT_VARS, projectId });
  }
  return variableSets;
};

const refetchCustomFieldTemplateQueries = projectId => [
  ...customFieldTemplateQueryVariables(projectId).map(variables => ({
    query: CUSTOM_FIELD_TEMPLATES_QUERY,
    ...(Object.keys(variables).length ? { variables } : {}),
  })),
  ...(projectId ? [{ query: FETCH_PROJECT_QUERY, variables: { id: projectId } }] : []),
];

const writeCreatedTemplateToCaches = (cache, template, projectId) => {
  if (!template) return;

  customFieldTemplateQueryVariables(projectId).forEach(variables => {
    cache.updateQuery(
      {
        query: CUSTOM_FIELD_TEMPLATES_QUERY,
        ...(Object.keys(variables).length ? { variables } : {}),
      },
      existing => {
        if (!existing?.customFieldTemplates) return existing;
        if (existing.customFieldTemplates.some(item => item.id === template.id)) {
          return existing;
        }
        return {
          ...existing,
          customFieldTemplates: [...existing.customFieldTemplates, template],
        };
      }
    );
  });

  if (!projectId) return;

  const fieldKey = `custom_field_${template.id}`;
  cache.updateQuery(
    { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    existing => {
      if (!existing?.project) return existing;
      return {
        ...existing,
        project: {
          ...existing.project,
          fieldSettings: { ...existing.project.fieldSettings, [fieldKey]: true },
          receivingSettings: { ...existing.project.receivingSettings, [fieldKey]: true },
        },
      };
    }
  );
};

const styles = {
  dialogTitle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  dialogContent: {
    paddingTop: 3,
  },
  formField: {
    marginBottom: 2,
  },
  actions: {
    px: 3,
    py: 2,
  },
};

const fieldTypes = [
  ["string", "Text"],
  ["decimal", "Number"],
  ["boolean", "Yes/No Toggle"],
];

const validationSchema = Yup.object().shape({
  fieldType: Yup.string().required("Field Type is required"),
  defaultLabel: Yup.string().required("Default Label is required"),
});

export default function ConfigurableFieldDialog({ onClose, onCreated, enabledTemplateIds = [] }) {
  const [step, setStep] = useState(1);
  const [showAssignExistingDialog, setShowAssignExistingDialog] = useState(false);
  const { projectId } = useParams();
  const [createCustomFieldTemplate, { loading }] = useMutation(CUSTOM_FIELD_TEMPLATE_CREATE, {
    refetchQueries: refetchCustomFieldTemplateQueries(projectId),
    awaitRefetchQueries: true,
    update(cache, { data }) {
      writeCreatedTemplateToCaches(
        cache,
        data?.customFieldTemplateCreate?.customFieldTemplate,
        projectId
      );
    },
  });
  const { notifySuccess, notifyError } = useNotification();

  const handleSubmit = async values => {
    try {
      const variables = {
        defaultLabel: values.defaultLabel,
        defaultValue: values.defaultValue,
        fieldType: values.fieldType,
        availableOptions: values.availableOptions,
        autoAddCustomValues: values.autoAddCustomValues,
        restrictToAvailableOptions: values.restrictToAvailableOptions,
        enableBarcodeScanner: values.enableBarcodeScanner,
        multipleValues: values.multipleValues,
        projectId,
      };

      await createCustomFieldTemplate({
        variables,
        onSuccess: ({ customFieldTemplate }) => {
          if (onCreated) onCreated(customFieldTemplate);
          notifySuccess("Configurable field created successfully");
          handleClose();
        },
        onFailure: errors => {
          notifyError(errors);
        },
      });
    } catch (error) {
      console.error("Error creating custom field:", error);
      notifyError("Failed to create configurable field");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleOpenAssignExistingDialog = () => {
    setShowAssignExistingDialog(true);
  };

  const handleCloseAssignExistingDialog = () => {
    setShowAssignExistingDialog(false);
  };

  const handleAssignExistingTemplate = async customFieldTemplate => {
    try {
      if (onCreated) {
        await onCreated(customFieldTemplate, { saveImmediately: true });
      }
      handleCloseAssignExistingDialog();
      handleClose();
    } catch (error) {
      console.error("Error assigning existing custom field:", error);
      notifyError("Failed to add configurable field");
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          fieldType: "",
          defaultLabel: "",
          defaultValue: "",
          availableOptions: [],
          autoAddCustomValues: false,
          restrictToAvailableOptions: false,
          enableBarcodeScanner: false,
          multipleValues: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, isValid, handleSubmit }) => (
          <Form>
            <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
              <DialogTitle sx={styles.dialogTitle}>
                <Typography fontSize={20} fontWeight={500} color="textPrimary">
                  Add New Configurable Field
                </Typography>
                <IconButton onClick={handleClose} sx={styles.closeButton}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={styles.dialogContent}>
                {step === 1 ? (
                  <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                    <Typography fontSize={16} fontWeight={400} color="textPrimary" mb={3}>
                      Which type of field would you like to create?
                    </Typography>
                    <Box display="flex" flexDirection="column" width="100%" maxWidth={300} gap={2}>
                      {fieldTypes.map(([type, label]) => (
                        <Button
                          key={type}
                          variant={values.fieldType === type ? "contained" : "outlined"}
                          size="large"
                          onClick={() => {
                            setFieldValue("fieldType", type);
                            setStep(2);
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <ConfigurableFieldFormStep />
                )}
              </DialogContent>
              <LinearProgress variant="determinate" value={step * 50} />
              <DialogActions sx={styles.actions}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box>
                    {step === 1 && projectId ? (
                      <Button variant="text" onClick={handleOpenAssignExistingDialog}>
                        Use Existing Field
                      </Button>
                    ) : null}
                  </Box>
                  {step === 1 ? (
                    <Button
                      variant="contained"
                      type="button"
                      disabled={!values.fieldType}
                      onClick={() => setStep(2)}
                    >
                      Next
                    </Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button variant="outlined" type="button" onClick={() => setStep(1)}>
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        onClick={handleSubmit}
                        disabled={loading || !isValid || Object.keys(errors).length > 0}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        Submit
                      </Button>
                    </Box>
                  )}
                </Box>
              </DialogActions>
            </Dialog>
          </Form>
        )}
      </Formik>
      {showAssignExistingDialog ? (
        <AssignExistingConfigurableFieldDialog
          projectId={projectId}
          enabledTemplateIds={enabledTemplateIds}
          onClose={handleCloseAssignExistingDialog}
          onSelect={handleAssignExistingTemplate}
        />
      ) : null}
    </>
  );
}
